/**
 * Utility functions for Video Metrics Service
 */
/**
 * Get hostname (from Display Ads)
 */
const maybe = (fn) => {
    try {
        return fn();
    }
    catch (e) {
        return;
    }
};
function getHostnameDirty() {
    const { hostname } = window.location;
    // Ad is not in iFrame
    if (window === window.top)
        return hostname;
    // Ad is in XML iFrame
    if (hostname.includes('arkadiumarena.com') || hostname.includes('rtl.de'))
        return hostname;
    // Ad is in Other iFrame (they-sell passback, etc.)
    return maybe(() => window.top?.location.hostname)
        || maybe(() => (new URL(document.referrer)).hostname)
        || 'default';
}
export function getHostname() {
    const hostname = getHostnameDirty();
    return hostname.includes('googlesyndication') ? 'safeframe.googlesyndication.com' : hostname;
}
/**
 * Generate random hex ID
 */
export function generateHexId(byteCount) {
    try {
        const bytes = new Uint8Array(byteCount);
        if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
            crypto.getRandomValues(bytes);
        }
        else {
            for (let i = 0; i < byteCount; i++) {
                bytes[i] = Math.floor(Math.random() * 256);
            }
        }
        return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    }
    catch {
        // Extremely defensive: fallback to Math.random when crypto fails
        let out = '';
        for (let i = 0; i < byteCount; i++) {
            out += Math.floor(Math.random() * 256).toString(16).padStart(2, '0');
        }
        return out;
    }
}
/**
 * FNV-1a hash for stable sampling
 */
export function hash32(s) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    return h >>> 0;
}
/**
 * Stable sampling based on hash
 */
export function stableSample(key, rate) {
    return hash32(key) / 0xffffffff < rate;
}
/**
 * Convert bytes to megabytes
 */
export function bytesToMb(bytes) {
    if (typeof bytes !== 'number' || !Number.isFinite(bytes))
        return;
    return Math.round((bytes / 1048576) * 100) / 100; // 2 decimal places
}
/**
 * Sanitize path (remove potential PII)
 */
export function sanitizePath(path) {
    try {
        // Replace sequences of 2+ digits with hashes
        return path.replace(/\d{2,}/g, match => '#'.repeat(Math.min(match.length, 6)));
    }
    catch {
        return path;
    }
}
/**
 * Get browser info from user agent
 */
export function getBrowserInfo() {
    try {
        const ua = navigator.userAgent;
        const edgeMatch = ua.match(/Edg\/(\d+\.\d+)/);
        if (edgeMatch)
            return { name: 'Edge', version: edgeMatch[1] };
        if (ua.includes('Chrome')) {
            const chromeMatch = ua.match(/Chrome\/(\d+\.\d+)/);
            return { name: 'Chrome', version: chromeMatch?.[1] };
        }
        if (ua.includes('Safari') && !ua.includes('Chrome')) {
            const safariMatch = ua.match(/Version\/(\d+\.\d+)/);
            return { name: 'Safari', version: safariMatch?.[1] };
        }
        const firefoxMatch = ua.match(/Firefox\/(\d+\.\d+)/);
        if (firefoxMatch)
            return { name: 'Firefox', version: firefoxMatch[1] };
        return { name: 'Other' };
    }
    catch {
        return { name: 'Other' };
    }
}
/**
 * Type guard for finite numbers
 */
export function isFiniteNumber(n) {
    return typeof n === 'number' && Number.isFinite(n);
}
/**
 * Helper for script source detection (non-throwing)
 */
export function getCurrentScriptSrc() {
    try {
        const script = document.currentScript;
        if (script instanceof HTMLScriptElement && script.src)
            return script.src;
    }
    catch { }
    return undefined;
}
